export const ARTICLE_COMBINES = {
  path: "/combines",
  category: "Comprendre les paris",
  title: "Pari combiné : fonctionnement, calcul de la cote totale et risques",
  description:
    "Qu'est-ce qu'un pari combiné ? Calcul de la cote totale, du gain potentiel et de la probabilité implicite, risques réels et simulateur gratuit.",
  h1: "Le pari combiné expliqué simplement",
  intro:
    "Le pari combiné est l'un des formats les plus populaires — et l'un des moins bien compris. Il promet des cotes élevées, mais chaque sélection ajoutée fait chuter la probabilité de gagner. Voici comment il fonctionne, chiffres à l'appui.",
  sections: [
    {
      id: "definition",
      h2: "Qu'est-ce qu'un pari combiné ?",
      blocks: [
        {
          t: "p",
          html: "Un pari combiné regroupe <strong>plusieurs paris individuels (appelés « sélections ») en un seul pari</strong>. Pour que le combiné soit gagnant, <strong>toutes</strong> les sélections doivent être exactes : une seule sélection perdante et l'ensemble du pari est perdu.",
        },
        {
          t: "p",
          html: "En contrepartie de cette exigence, les cotes des sélections se multiplient entre elles, ce qui produit une cote totale bien plus élevée que celle d'un pari simple.",
        },
      ],
    },
    {
      id: "fonctionnement",
      h2: "Comment fonctionne un combiné ?",
      blocks: [
        {
          t: "ol",
          items: [
            "Vous choisissez plusieurs événements (par exemple trois matchs de football).",
            "Vous faites une sélection sur chacun (victoire de A, match nul, plus de 2,5 buts...).",
            "L'opérateur multiplie les cotes de vos sélections pour obtenir la <strong>cote totale</strong>.",
            "Vous engagez une mise unique sur l'ensemble du combiné.",
            "Si toutes les sélections sont gagnantes, vous remportez mise × cote totale. Si l'une d'elles est perdante, vous perdez la totalité de la mise.",
          ],
        },
      ],
    },
    {
      id: "calcul-cote",
      h2: "Comment calculer la cote totale",
      blocks: [
        {
          t: "p",
          html: "<strong>Cote totale = cote 1 × cote 2 × cote 3 × ...</strong>",
        },
        {
          t: "p",
          html: "Exemple : trois sélections aux cotes 1,80, 2,10 et 1,50 donnent une cote totale de 1,80 × 2,10 × 1,50 = <strong>5,67</strong>.",
        },
        { t: "diagram", name: "combi" },
      ],
    },
    {
      id: "calcul-gain",
      h2: "Comment calculer le gain potentiel",
      blocks: [
        {
          t: "p",
          html: "<strong>Gain total = mise × cote totale</strong> (mise incluse) et <strong>gain net = mise × (cote totale − 1)</strong>.",
        },
        {
          t: "p",
          html: "Pour 10 € misés sur le combiné ci-dessus à la cote 5,67 : gain total de <strong>56,70 €</strong>, soit un gain net de 46,70 €. Si une seule des trois sélections est perdante, les 10 € sont perdus.",
        },
      ],
    },
    {
      id: "probabilites",
      h2: "Les probabilités implicites dans un combiné",
      blocks: [
        {
          t: "p",
          html: "Comme pour un pari simple, on peut convertir la cote totale en probabilité : <strong>probabilité implicite globale = 1 ÷ cote totale</strong>.",
        },
        {
          t: "p",
          html: "Pour notre exemple : 1 ÷ 5,67 = <strong>17,6 %</strong>. Autrement dit, selon les cotes de l'opérateur, ce combiné a moins d'une chance sur cinq d'être gagnant — alors que chaque sélection prise isolément semblait « probable ».",
        },
        {
          t: "p",
          html: "Les probabilités se multiplient aussi : 55,6 % × 47,6 % × 66,7 % ≈ 17,6 %. C'est la mécanique du combiné : chaque sélection supplémentaire réduit la probabilité globale, même si la cote totale augmente.",
        },
      ],
    },
    {
      id: "risque",
      h2: "Pourquoi le risque augmente avec le nombre de sélections",
      blocks: [
        {
          t: "p",
          html: "Ajouter une sélection, même à faible cote, ajoute un événement qui peut échouer. Un combiné de 6 sélections dont chacune a 80 % de chances de réussir n'a en réalité qu'environ <strong>26 %</strong> de chances d'être gagnant (0,8⁶).",
        },
        {
          t: "callout",
          kind: "warn",
          html: "La cote élevée d'un combiné n'est pas une opportunité : elle traduit mathématiquement une faible probabilité de succès. Plus il y a de sélections, plus la probabilité que le pari soit gagnant diminue.",
        },
        {
          t: "p",
          html: "À noter également : la marge de l'opérateur s'applique à chaque sélection. Sur un combiné, elle se cumule, ce qui aggrave encore l'espérance de gain du joueur (voir <a href=\"/comprendre/les-maths-du-long-terme\" class=\"font-semibold text-[#2BB08A] underline transition-colors hover:text-[#0B1F3A]\">les maths du long terme</a>).",
        },
      ],
    },
    {
      id: "erreurs",
      h2: "Les erreurs fréquentes avec les combinés",
      blocks: [
        {
          t: "list",
          items: [
            "<strong>Regarder la cote totale sans regarder la probabilité.</strong> Une cote de 50 signifie environ 2 % de chances de gagner.",
            "<strong>Ajouter « une sélection sûre » pour gonfler la cote.</strong> Aucune sélection n'est sûre, et chaque ajout réduit la probabilité globale.",
            "<strong>Multiplier les combinés pour « se refaire » après une perte.</strong> C'est le mécanisme le plus courant d'endettement (voir <a href=\"/comprendre/erreurs-et-mythes\" class=\"font-semibold text-[#2BB08A] underline transition-colors hover:text-[#0B1F3A]\">erreurs et mythes</a>).",
            "<strong>Croire qu'un combiné « presque gagnant » était proche de l'être.</strong> Un combiné perdant à une sélection près est un combiné perdant : le résultat est binaire.",
          ],
        },
      ],
    },
    {
      id: "avant-de-jouer",
      h2: "Les points à comprendre avant d'utiliser un combiné",
      blocks: [
        {
          t: "list",
          items: [
            "Maîtriser d'abord <a href=\"/comprendre/les-cotes\" class=\"font-semibold text-[#2BB08A] underline transition-colors hover:text-[#0B1F3A]\">le fonctionnement des cotes</a> et la notion de probabilité implicite.",
            "Comprendre la différence avec <a href=\"/comprendre/les-types-de-paris\" class=\"font-semibold text-[#2BB08A] underline transition-colors hover:text-[#0B1F3A]\">les autres types de paris</a>, notamment le pari système, qui tolère une ou plusieurs erreurs.",
            "Fixer un budget perdu d'office et ne jamais le dépasser (voir <a href=\"/jeu-responsable\" class=\"font-semibold text-[#2BB08A] underline transition-colors hover:text-[#0B1F3A]\">jeu responsable</a>).",
            "Vérifier les conditions éventuelles : certains bonus excluent ou imposent les combinés (voir <a href=\"/demarrer/bonus-de-bienvenue\" class=\"font-semibold text-[#2BB08A] underline transition-colors hover:text-[#0B1F3A]\">bonus de bienvenue</a>).",
          ],
        },
      ],
    },
    {
      id: "simulateur-combine",
      h2: "Simulateur de combiné",
      blocks: [
        {
          t: "p",
          html: "Composez un combiné fictif de 2 à 6 sélections et observez en direct la cote totale, le gain potentiel et la probabilité implicite globale.",
        },
        { t: "tool", name: "combi" },
        {
          t: "callout",
          kind: "retain",
          html: "Un combiné multiplie les cotes, mais divise les chances : la probabilité de gagner chute à chaque sélection ajoutée. Il doit rester un divertissement occasionnel, jamais une stratégie.",
        },
      ],
    },
  ],
  faq: [
    {
      q: "Un combiné peut-il être gagnant si une sélection est perdante ?",
      a: "Non. Toutes les sélections d'un combiné doivent être gagnantes. Seul le pari système permet d'être payé malgré une ou plusieurs sélections perdantes.",
    },
    {
      q: "Quelle est la différence entre un combiné et un pari système ?",
      a: "Le combiné exige que toutes les sélections soient gagnantes. Le système génère plusieurs paris à partir de vos sélections et peut rapporter quelque chose même en cas d'erreur, mais la mise totale est multipliée par le nombre de paris générés.",
    },
    {
      q: "Combien de sélections peut-on mettre dans un combiné ?",
      a: "Cela dépend des opérateurs, souvent une vingtaine au maximum. Notre simulateur pédagogique se limite volontairement à 6 sélections pour rester lisible.",
    },
  ],
};
